/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-misused-promises */

import { useCallback, useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Text } from 'foundations-components/components/Text';
import { Form } from 'foundations-components/transitional/components/Form';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { Button } from 'foundations-components/transitional/components/Button';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { getPropertyValue } from 'marketing-platform-lib/utils/crmUtils';
import { FORM_ACTION_IDS } from 'asset-actions-lib-forms/constants/actionIds';
import { useAssetActions } from 'asset-actions-lib/context/AssetActionsContext';
import FormCloningWarningWithFlows from 'asset-actions-lib-forms/clone/components/FormCloningWarningWithFlows';
import FormNameValidationMessage from 'asset-actions-lib-forms/clone/components/FormNameValidationMessage';
import BusinessUnitSelector from 'asset-actions-lib-forms/clone/components/BusinessUnitSelector';
import useCloneForm from 'asset-actions-lib-forms/clone/hooks/useCloneForm';
import FormsCampaignSelect from 'asset-actions-lib-forms/selectCampaign/FormsCampaignSelect';
import * as CampaignsAssociation from 'campaigns-lib/campaignsPlatform/api/CampaignsAssociation';
import { isCampaignEligibleFormType } from 'asset-actions-lib-forms/utils/campaignUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_NAME_LENGTH = 255;
const DEFAULT_BUSINESS_UNIT_ID = 0;
function getDefaultFormName(formName) {
  return I18n.text(`assetActionsLibForms.modals.cloneDialog.defaultFormName`, {
    originalFormName: I18n.SafeString(formName)
  });
}
function getIsNameValid(name) {
  return Boolean(name && name.length > 0 && name.length < MAX_NAME_LENGTH);
}
export default function CloneFormDialog({
  crmObject,
  onClose,
  scopes,
  onConfirm
}) {
  var _ref;
  const {
    onActionError
  } = useAssetActions();
  const {
    cloneForm,
    loading,
    error,
    clearError
  } = useCloneForm();
  const hasBusinessUnitAccess = scopes.includes('business-units-access');
  const hasCampaignsReadAccess = scopes.includes('campaigns-access');
  const hasCampaignsWriteAccess = scopes.includes('campaigns-write');
  const originalFormName = String(crmObject ? getPropertyValue(crmObject, 'hs_name', '') : '');
  const formGuid = String(crmObject ? getPropertyValue(crmObject, 'hs_form_id', '') : '');
  const formType = String(crmObject ? getPropertyValue(crmObject, 'hs_form_type', '') : '');
  const embedVersion = String(crmObject ? getPropertyValue(crmObject, 'hs_embed_version', '') : '');
  const [businessUnitId, setBusinessUnitId] = useState((_ref = crmObject && Number(getPropertyValue(crmObject, 'hs_all_assigned_business_unit_ids'))) !== null && _ref !== void 0 ? _ref : DEFAULT_BUSINESS_UNIT_ID);
  const marketingCampaignGuid = String(crmObject ? getPropertyValue(crmObject, 'hs_marketing_campaign_guid', '') : '');
  const [updatedName, setUpdatedName] = useState(getDefaultFormName(originalFormName));
  const [errorCode, setErrorCode] = useState('');
  const [selectedCampaignGuid, setSelectedCampaignGuid] = useState(marketingCampaignGuid);
  const [isSavingCampaign, setIsSavingCampaign] = useState(false);
  const isNameValid = getIsNameValid(updatedName);
  const hasErrors = Boolean(error || !isNameValid);
  const handleBusinessUnitChange = buValue => {
    setBusinessUnitId(buValue !== null && buValue !== void 0 ? buValue : DEFAULT_BUSINESS_UNIT_ID);
  };
  const handleNameChange = event => {
    setUpdatedName(event.target.value);
    if (errorCode === 'DUPLICATE_NAME') {
      clearError();
      setErrorCode('');
    }
  };
  const handleSubmit = useCallback(() => {
    return cloneForm({
      businessUnitId: businessUnitId ? parseInt(`${businessUnitId}`, 10) : 0,
      formType: formType,
      embedVersion,
      guid: formGuid,
      name: updatedName
    }).then(async clonedForm => {
      const clonedFormGuid = clonedForm === null || clonedForm === void 0 ? void 0 : clonedForm.id;
      if (selectedCampaignGuid) {
        setIsSavingCampaign(true);
        try {
          await CampaignsAssociation.saveCampaign({
            objectType: '0-28',
            crmIdProperty: 'hs_form_id',
            selectedAssetCrmId: clonedFormGuid,
            currentCampaignGuids: [],
            updatedCampaignGuids: [selectedCampaignGuid]
          });
        } catch (campaignError) {
          if (onActionError) {
            onActionError({
              actionId: FORM_ACTION_IDS.CLONE,
              error: campaignError
            });
          }
        } finally {
          setIsSavingCampaign(false);
        }
      }

      // Always proceed with clone completion
      onConfirm(FORM_ACTION_IDS.CLONE, {
        clonedForm
      });
    }).catch(actionError => {
      if (!onActionError) {
        return;
      }
      onActionError({
        actionId: FORM_ACTION_IDS.CLONE,
        error: actionError
      });
      if (actionError.networkError && actionError.networkError.status) {
        const statusCode = actionError.networkError.status;
        switch (statusCode) {
          case 0:
            setErrorCode('NO_NETWORK');
            break;
          case 404:
            setErrorCode('NOT_FOUND');
            break;
          case 409:
            setErrorCode('DUPLICATE_NAME');
            break;
          default:
            setErrorCode('GENERIC_ERROR');
            break;
        }
      }
    });
  }, [businessUnitId, cloneForm, embedVersion, formGuid, formType, onActionError, onConfirm, updatedName, selectedCampaignGuid]);
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "form-clone-dialog",
    onEsc: onClose,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "assetActionsLibForms.modals.cloneDialog.header"
        })
      })]
    }), /*#__PURE__*/_jsx(Form, {
      onSubmit: handleSubmit,
      children: /*#__PURE__*/_jsxs(DialogBody, {
        children: [/*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "assetActionsLibForms.modals.cloneDialog.introduction"
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "assetActionsLibForms.modals.cloneDialog.formNameLabel"
          }),
          error: hasErrors,
          validationMessage: /*#__PURE__*/_jsx(FormNameValidationMessage, {
            errorCode: errorCode,
            isNameValid: isNameValid
          }),
          children: /*#__PURE__*/_jsx(TextInput, {
            "data-test-id": "clone-form-name",
            value: updatedName,
            onChange: handleNameChange
          })
        }), hasBusinessUnitAccess && /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "assetActionsLibForms.modals.cloneDialog.businessUnit_buv2"
          }),
          children: /*#__PURE__*/_jsx(BusinessUnitSelector, {
            value: businessUnitId || DEFAULT_BUSINESS_UNIT_ID,
            onChange: handleBusinessUnitChange
          })
        }), hasCampaignsReadAccess && isCampaignEligibleFormType(formType) && /*#__PURE__*/_jsx(FormControl, {
          children: /*#__PURE__*/_jsx(FormsCampaignSelect, {
            formGuid: formGuid,
            hasCampaignsReadAccess: hasCampaignsReadAccess,
            hasCampaignsWriteAccess: hasCampaignsWriteAccess,
            selectedCampaignGuid: selectedCampaignGuid,
            onChange: setSelectedCampaignGuid
          })
        }), /*#__PURE__*/_jsx(FormCloningWarningWithFlows, {
          formGuid: formGuid,
          formType: formType,
          hasBusinessUnitAccess: hasBusinessUnitAccess,
          isExternalEditor: false
        })]
      })
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        disabled: loading || hasErrors || isSavingCampaign,
        onClick: handleSubmit,
        "data-test-id": "clone-form-button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "assetActionsLibForms.modals.cloneDialog.cloneCta"
        })
      }), /*#__PURE__*/_jsx(Button, {
        "data-test-id": "clone-form-cancel-button",
        use: "secondary",
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "assetActionsLibForms.modals.cloneDialog.cancelCta"
        })
      })]
    })]
  });
}